// components/AnalyticsDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from "react-router-dom";
import { 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Paper,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  LinearProgress,
  Alert,
  Snackbar,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ComposedChart,
  Line,
  Area,
  LineChart,
  AreaChart
} from 'recharts';
import {
  TrendingUp,
  People,
  ShoppingCart,
  AttachMoney,
  Visibility,
  Category,
  Devices,
  Download,
  Refresh,
  DateRange,
  Timeline,
  RemoveShoppingCart,
  Add,
  Warning,
  CheckCircle,
  AddShoppingCart,
  InstallDesktop,
  RocketLaunch,
  Repeat,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import TrendingFlat from '@mui/icons-material/TrendingFlat';
import TrendingDown from '@mui/icons-material/TrendingDown';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format, subDays, subYears, startOfDay, endOfDay } from 'date-fns';
import axios from 'axios';
import './AnalyticsDashboard.css';

const ADMIN_ID = "657d60c2e11554ed0ddbdc31";

// Color palettes
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#a4de6c', '#d0ed57'];

// Time periods
const TIME_PERIODS = [
  { label: 'Today', value: 'today', days: 1 },
  { label: 'Last 7 Days', value: '7d', days: 7 },
  { label: 'Last 30 Days', value: '30d', days: 30 },
  { label: 'Last 90 Days', value: '90d', days: 90 },
  { label: 'This Month', value: 'month', days: 30 },
  { label: 'Last Month', value: 'last_month', days: 30 },
  { label: 'This Quarter', value: 'quarter', days: 90 },
  { label: 'This Year', value: 'year', days: 365 },
  { label: 'Custom', value: 'custom', days: null }
];

const AnalyticsDashboard = () => {
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [timePeriod, setTimePeriod] = useState('30d');
  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [cartData, setCartData] = useState(null);
  const [purchaseData, setPurchaseData] = useState(null);
  const [loadingCart, setLoadingCart] = useState(false);
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [productTimePeriod, setProductTimePeriod] = useState('30d');
  const [productData, setProductData] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [pwaData, setPwaData] = useState(null);
  const [loadingPWA, setLoadingPWA] = useState(false);

  // Check if user is admin
  const isAdmin = userId === ADMIN_ID;

  // Format dates for API
  const formatDateForAPI = (date) => format(date, 'yyyy-MM-dd');

  const prepareProductTimeSeriesData = () => {
  if (!productData?.timeSeries) return [];
  
  return productData.timeSeries.map(item => ({
    date: item._id,
    views: item.totalViews || 0,
    addsToCart: item.totalAddsToCart || 0,
    purchases: item.totalPurchases || 0,
    revenue: item.totalRevenue || 0
  }));
};
  
    // Fetch analytics data
  const fetchAnalyticsData = useCallback(async () => {
    if (!isAdmin) return;

    // Date calculation logic moved inside useCallback
    const calculateDateRangeForFetch = (period) => {
      const now = new Date();
      let start, end = now;

      switch (period) {
        case 'today':
          start = startOfDay(now);
          end = endOfDay(now);
          break;
        case '7d':
          start = subDays(now, 7);
          break;
        case '30d':
          start = subDays(now, 30);
          break;
        case '90d':
          start = subDays(now, 90);
          break;
        case 'month':
          start = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'last_month':
          start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          end = new Date(now.getFullYear(), now.getMonth(), 0);
          break;
        case 'quarter':
          start = subDays(now, 90);
          break;
        case 'year':
          start = subYears(now, 1);
          break;
        case 'custom':
          // Use selected dates
          start = startDate;
          end = endDate;
          break;
        default:
          start = subDays(now, 30);
      }

      return {
        startDate: formatDateForAPI(start),
        endDate: formatDateForAPI(end)
      };
    };

    setLoading(true);
    setError(null);

    try {
      const { startDate: start, endDate: end } = calculateDateRangeForFetch(timePeriod);
      
      // Fetch multiple endpoints in parallel
      const [
        dashboardSummary,
        dailyReport,
        popularProducts,
        categoryPerformance,
        deviceStats,
        userEngagement,
        realtimeStats
      ] = await Promise.all([
        axios.get(`https://insightdata-27a8ba1af1b4.herokuapp.com/api/admin/analytics/dashboard/summary?startDate=${start}&endDate=${end}`, {
          headers: { 'X-Admin-Key': process.env.REACT_APP_ADMIN_API_KEY || 'admin-key' }
        }),
        axios.get(`https://insightdata-27a8ba1af1b4.herokuapp.com/api/admin/analytics/reports/daily?date=${formatDateForAPI(new Date())}`, {
          headers: { 'X-Admin-Key': process.env.REACT_APP_ADMIN_API_KEY || 'admin-key' }
        }),
        axios.get(`https://insightdata-27a8ba1af1b4.herokuapp.com/api/admin/analytics/products/popular?startDate=${start}&endDate=${end}&limit=10`, {
          headers: { 'X-Admin-Key': process.env.REACT_APP_ADMIN_API_KEY || 'admin-key' }
        }),
        axios.get(`https://insightdata-27a8ba1af1b4.herokuapp.com/api/admin/analytics/categories/performance?startDate=${start}&endDate=${end}`, {
          headers: { 'X-Admin-Key': process.env.REACT_APP_ADMIN_API_KEY || 'admin-key' }
        }),
        axios.get(`https://insightdata-27a8ba1af1b4.herokuapp.com/api/admin/analytics/devices/stats?startDate=${start}&endDate=${end}`, {
          headers: { 'X-Admin-Key': process.env.REACT_APP_ADMIN_API_KEY || 'admin-key' }
        }),
        axios.get(`https://insightdata-27a8ba1af1b4.herokuapp.com/api/admin/analytics/users/engagement?startDate=${start}&endDate=${end}`, {
          headers: { 'X-Admin-Key': process.env.REACT_APP_ADMIN_API_KEY || 'admin-key' }
        }),
        axios.get(`https://insightdata-27a8ba1af1b4.herokuapp.com/api/analytics/realtime/stats`),
      ]);

      setData({
        overview: dashboardSummary.data?.data?.overview || {},
        dailyReport: dailyReport.data?.data || [],
        popularProducts: popularProducts.data?.data || [],
        categories: categoryPerformance.data?.data || [],
        devices: deviceStats.data?.data || [],
        userEngagement: userEngagement.data?.data || [],
        realtime: realtimeStats.data?.data || {}
      });

      setSnackbar({
        open: true,
        message: 'Analytics data loaded successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.message || 'Failed to load analytics data');
      setSnackbar({
        open: true,
        message: 'Failed to load analytics data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [isAdmin, timePeriod, startDate, endDate]);

  // Initial fetch and refetch when time period changes
  useEffect(() => {
    if (isAdmin) {
      fetchAnalyticsData();
    }
  }, [isAdmin, fetchAnalyticsData]);

const fetchCartAnalytics = async () => {
  setLoadingCart(true);
  try {
    const response = await axios.get(`https://insightdata-27a8ba1af1b4.herokuapp.com/api/analytics/cart/last-hour`, {
      headers: { 'X-Admin-Key': process.env.REACT_APP_ADMIN_API_KEY || 'admin-key' }
    });
    setCartData(response.data.data);
  } catch (error) {
    console.error('Error fetching cart analytics:', error);
    setSnackbar({
      open: true,
      message: 'Failed to load cart analytics',
      severity: 'error'
    });
  } finally {
    setLoadingCart(false);
  }
};

const fetchPurchaseAnalytics = async () => {
  setLoadingPurchases(true);
  try {
    const response = await axios.get(`https://insightdata-27a8ba1af1b4.herokuapp.com/api/analytics/purchases/last-hour`, {
      headers: { 'X-Admin-Key': process.env.REACT_APP_ADMIN_API_KEY || 'admin-key' }
    });
    setPurchaseData(response.data.data);
  } catch (error) {
    console.error('Error fetching purchase analytics:', error);
    setSnackbar({
      open: true,
      message: 'Failed to load purchase analytics',
      severity: 'error'
    });
  } finally {
    setLoadingPurchases(false);
  }
};

// Add this fetch function
const fetchProductTimeAnalytics = async () => {
  setLoadingProducts(true);
  try {
    const response = await axios.get(
      `https://insightdata-27a8ba1af1b4.herokuapp.com/api/admin/analytics/products/time-analytics?period=${productTimePeriod}`, 
      {
        headers: { 'X-Admin-Key': process.env.REACT_APP_ADMIN_API_KEY || 'admin-key' }
      }
    );
    setProductData(response.data.data);
  } catch (error) {
    console.error('Error fetching product time analytics:', error);
    setSnackbar({
      open: true,
      message: 'Failed to load product time analytics',
      severity: 'error'
    });
  } finally {
    setLoadingProducts(false);
  }
};

// Add these helper functions
const preparePWATimelineData = () => {
  if (!pwaData?.summary?.dailyTrends) return [];
  
  return pwaData.summary.dailyTrends.map(item => ({
    date: format(new Date(item._id), 'MMM dd'),
    installs: item.installs || 0,
    launches: item.launches || 0
  }));
};

const preparePWADeviceData = () => {
  if (!pwaData?.summary?.deviceBreakdown) return [];
  
  return pwaData.summary.deviceBreakdown.map(device => ({
    device: device._id || 'Unknown',
    installs: device.installs || 0,
    launches: device.launches || 0
  })).sort((a, b) => b.installs - a.installs);
};

const preparePWAPlatformData = () => {
  if (!pwaData?.summary?.platformBreakdown) return [];
  
  return pwaData.summary.platformBreakdown.map(platform => ({
    platform: platform._id || 'Unknown',
    installs: platform.installs || 0,
    launches: platform.launches || 0
  })).sort((a, b) => b.launches - a.launches);
};

const getPWATrendIcon = (day) => {
  const installs = day.installs?.count || 0;
  const launches = day.launches?.count || 0;
  
  if (launches > installs * 2) {
    return <TrendingUp color="success" />;
  } else if (launches > installs) {
    return <TrendingUp color="info" />;
  } else if (launches === installs) {
    return <TrendingFlat color="warning" />;
  } else {
    return <TrendingDown color="error" />;
  }
};

// Add fetch function
const fetchPWAnalytics = async () => {
  setLoadingPWA(true);
  try {
    const response = await axios.get(
      `https://insightdata-27a8ba1af1b4.herokuapp.com/api/admin/analytics/pwa/analytics?startDate=${formatDateForAPI(startDate)}&endDate=${formatDateForAPI(endDate)}`, 
      {
        headers: { 'X-Admin-Key': process.env.REACT_APP_ADMIN_API_KEY || 'admin-key' }
      }
    );
    setPwaData(response.data.data);
  } catch (error) {
    console.error('Error fetching PWA analytics:', error);
    setSnackbar({
      open: true,
      message: 'Failed to load PWA analytics',
      severity: 'error'
    });
  } finally {
    setLoadingPWA(false);
  }
};

// Fetch data when tab changes
useEffect(() => {
  if (activeTab === 6 && !cartData) {
    fetchCartAnalytics();
  }
  if (activeTab === 7 && !purchaseData) {
    fetchPurchaseAnalytics();
  }
}, [activeTab, cartData, purchaseData]);
 
 // Loading components for new tabs
if (activeTab === 6 && loadingCart) {
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <LinearProgress sx={{ mb: 2 }} />
      <Typography>Loading cart analytics...</Typography>
    </Container>
  );
}

if (activeTab === 7 && loadingPurchases) {
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <LinearProgress sx={{ mb: 2 }} />
      <Typography>Loading purchase analytics...</Typography>
    </Container>
  );
}
  // Handle time period change
  const handleTimePeriodChange = (event) => {
    setTimePeriod(event.target.value);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle date range change for custom period
  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  // Format numbers with commas
  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  // Format currency
  const formatCurrency = (amount, currency = 'TZS') => {
    if (!amount) return `${currency} 0`;
    return `${currency} ${amount.toLocaleString()}`;
  };

  // Calculate percentage change
  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return 100;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  // Generate chart data for time series
  const generateTimeSeriesData = () => {
    if (!data?.dailyReport) return [];
    
    return data.dailyReport.map(item => ({
      time: item._id || 'N/A',
      interactions: item.total || 0,
      views: item.interactions?.find(i => i.type === 'product_view')?.count || 0,
      purchases: item.interactions?.find(i => i.type === 'purchase')?.count || 0,
      revenue: item.interactions?.find(i => i.type === 'purchase')?.revenue || 0
    }));
  };

  // Prepare category data for pie chart
  const prepareCategoryData = () => {
    if (!data?.categories) return [];
    
    return data.categories.slice(0, 8).map(cat => ({
      name: cat.category || 'Unknown',
      value: cat.revenue || cat.purchases || 0,
      views: cat.views || 0,
      purchases: cat.purchases || 0
    }));
  };

  // Prepare device data
  const prepareDeviceData = () => {
    if (!data?.devices) return [];
    
    return data.devices.map(device => ({
      device: device.deviceType || 'Unknown',
      users: device.uniqueUsersCount || 0,
      sessions: device.count || 0,
      purchases: device.purchases || 0,
      revenue: device.revenue || 0
    }));
  };

  // Prepare user engagement data
  const prepareUserEngagementData = () => {
    if (!data?.userEngagement) return [];
    
    return data.userEngagement.slice(0, 10).map(user => ({
      name: user.userName || user.userEmail || `User ${user.userId?.slice(-6)}`,
      engagement: Math.round(user.engagementScore || 0),
      sessions: user.sessionCount || 0,
      purchases: user.purchases || 0,
      revenue: user.revenue || 0
    }));
  };

  // Render metric card
  const MetricCard = ({ title, value, icon, change, subtitle, color = 'primary' }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography color="textSecondary" variant="subtitle2">
            {title}
          </Typography>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: `${color}.light`,
              color: `${color}.main`
            }}
          >
            {icon}
          </Box>
        </Box>
        <Typography variant="h4" component="div">
          {typeof value === 'number' ? formatNumber(value) : value}
        </Typography>
        {subtitle && (
          <Typography color="textSecondary" variant="caption">
            {subtitle}
          </Typography>
        )}
        {change !== undefined && (
          <Box display="flex" alignItems="center" mt={1}>
            <TrendingUp 
              sx={{ 
                fontSize: 16, 
                color: parseFloat(change) >= 0 ? 'success.main' : 'error.main',
                mr: 0.5 
              }} 
            />
            <Typography 
              variant="caption" 
              sx={{ 
                color: parseFloat(change) >= 0 ? 'success.main' : 'error.main',
                fontWeight: 'medium'
              }}
            >
              {change}%
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
              from previous period
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
    // Add loading state for products tab
if (activeTab === 1 && loadingProducts) {
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <LinearProgress sx={{ mb: 2 }} />
      <Typography>Loading product analytics...</Typography>
    </Container>
  );
}

  // Render loading skeleton
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Grid item xs={12} md={6} lg={4} key={item}>
              <Card>
                <CardContent>
                  <LinearProgress />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  // Render access denied for non-admin users
  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6">Access Denied</Typography>
          <Typography>
            You don't have permission to access the analytics dashboard.
            Only administrators can view this page.
          </Typography>
        </Alert>
        <Button 
          variant="contained" 
          href="/"
          startIcon={<DashboardIcon />}
        >
          Return to Home
        </Button>
      </Container>
    );
  }

  // Render error state
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6">Error Loading Analytics</Typography>
          <Typography>{error}</Typography>
        </Alert>
        <Button 
          variant="contained" 
          onClick={fetchAnalyticsData}
          startIcon={<Refresh />}
        >
          Retry
        </Button>
      </Container>
    );
  }

  // Main dashboard render
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Analytics Dashboard
            </Typography>
            <Typography color="textSecondary">
              Real-time insights and performance metrics
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={() => {
                // Export functionality
                setSnackbar({
                  open: true,
                  message: 'Export feature coming soon',
                  severity: 'info'
                });
              }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={fetchAnalyticsData}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Controls */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6} lg={4}>
              <FormControl fullWidth>
                <InputLabel>Time Period</InputLabel>
                <Select
                  value={timePeriod}
                  label="Time Period"
                  onChange={handleTimePeriodChange}
                >
                  {TIME_PERIODS.map((period) => (
                    <MenuItem key={period.value} value={period.value}>
                      {period.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {timePeriod === 'custom' && (
              <>
                <Grid item xs={12} md={3} lg={2}>
                  <DatePicker
                    selected={startDate}
                    onChange={handleDateChange}
                    startDate={startDate}
                    endDate={endDate}
                    selectsRange
                    customInput={
                      <Button 
                        variant="outlined" 
                        fullWidth
                        startIcon={<DateRange />}
                      >
                        {format(startDate, 'MMM dd, yyyy')}
                      </Button>
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3} lg={2}>
                  <DatePicker
                    selected={endDate}
                    onChange={handleDateChange}
                    startDate={startDate}
                    endDate={endDate}
                    selectsRange
                    customInput={
                      <Button 
                        variant="outlined" 
                        fullWidth
                        startIcon={<DateRange />}
                      >
                        {format(endDate, 'MMM dd, yyyy')}
                      </Button>
                    }
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12} md={6} lg={4}>
              <Box display="flex" gap={1}>
                <Chip 
                  icon={<Timeline />} 
                  label={`${formatDateForAPI(startDate)} to ${formatDateForAPI(endDate)}`}
                  color="primary"
                  variant="outlined"
                />
                <Chip 
                  label={`${data?.realtime?.activeSessions || 0} Active Sessions`}
                  color="success"
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
            <Tabs
      value={activeTab}
      onChange={handleTabChange}
      indicatorColor="primary"
      textColor="primary"
      variant="scrollable"
      scrollButtons="auto"
    >
      <Tab label="Overview" icon={<DashboardIcon />} iconPosition="start" />
      <Tab label="Products" icon={<ShoppingCart />} iconPosition="start" />
      <Tab label="Categories" icon={<Category />} iconPosition="start" />
      <Tab label="Users" icon={<People />} iconPosition="start" />
      <Tab label="Devices" icon={<Devices />} iconPosition="start" />
      <Tab label="Real-time" icon={<Timeline />} iconPosition="start" />
      <Tab label="Cart Analytics" icon={<ShoppingCart />} iconPosition="start" />
      <Tab label="Purchase Analytics" icon={<AttachMoney />} iconPosition="start" />
      <Tab label="PWA Analytics" icon={<InstallDesktop />} iconPosition="start" />
    </Tabs>      
</Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <>
          {/* Overview Metrics */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Events"
                value={data?.overview?.totalEvents || 0}
                icon={<Visibility />}
                change={calculateChange(data?.overview?.totalEvents || 0, 0)}
                color="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Unique Users"
                value={data?.overview?.uniqueUsers || 0}
                icon={<People />}
                change={calculateChange(data?.overview?.uniqueUsers || 0, 0)}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Purchases"
                value={data?.overview?.totalPurchases || 0}
                icon={<ShoppingCart />}
                change={calculateChange(data?.overview?.totalPurchases || 0, 0)}
                color="warning"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Revenue"
                value={formatCurrency(data?.overview?.totalRevenue || 0)}
                icon={<AttachMoney />}
                change={calculateChange(data?.overview?.totalRevenue || 0, 0)}
                color="primary"
              />
            </Grid>
          </Grid>

          {/* Charts Row 1 */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} lg={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Activity Timeline
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={generateTimeSeriesData()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="time" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Area 
                          type="monotone" 
                          dataKey="interactions" 
                          fill="#8884d8" 
                          fillOpacity={0.3} 
                          stroke="#8884d8" 
                          name="Interactions"
                        />
                        <Bar 
                          dataKey="purchases" 
                          fill="#82ca9d" 
                          name="Purchases"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="revenue" 
                          stroke="#ff7300" 
                          name="Revenue"
                          yAxisId={1}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} lg={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Top Categories
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={prepareCategoryData()}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => `${entry.name}: ${formatCurrency(entry.value)}`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {prepareCategoryData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts Row 2 */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Device Distribution
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={prepareDeviceData()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="device" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="sessions" fill="#8884d8" name="Sessions" />
                        <Bar dataKey="purchases" fill="#82ca9d" name="Purchases" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    User Engagement Score
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={prepareUserEngagementData()}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="name" />
                        <PolarRadiusAxis />
                        <Radar
                          name="Engagement"
                          dataKey="engagement"
                          stroke="#8884d8"
                          fill="#8884d8"
                          fillOpacity={0.6}
                        />
                        <Tooltip />
                        <Legend />
                      </RadarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {activeTab === 1 && (
  <Grid container spacing={3}>
    {/* Time Period Selector */}
    <Grid item xs={12}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Time Period</InputLabel>
              <Select
                value={productTimePeriod}
                label="Time Period"
                onChange={(e) => setProductTimePeriod(e.target.value)}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
                <MenuItem value="90d">Last 90 Days</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="quarter">This Quarter</MenuItem>
                <MenuItem value="year">This Year</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                onClick={fetchProductTimeAnalytics}
                startIcon={<Refresh />}
              >
                Refresh
              </Button>
              <Chip 
                label={`${productData?.dateRange?.start ? format(new Date(productData.dateRange.start), 'MMM dd, yyyy') : ''} - ${productData?.dateRange?.end ? format(new Date(productData.dateRange.end), 'MMM dd, yyyy') : ''}`}
                color="primary"
                variant="outlined"
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Grid>

    {/* Summary Metrics */}
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Product Performance Summary ({productTimePeriod.replace('d', ' Days').replace('month', 'Month').replace('quarter', 'Quarter').replace('year', 'Year').replace('today', 'Today')})
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <MetricCard
                title="Total Views"
                value={productData?.totals?.totalViews || 0}
                icon={<Visibility />}
                subtitle={`${(productData?.dailyAverages?.avgViewsPerDay || 0).toFixed(1)} avg/day`}
                color="info"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MetricCard
                title="Adds to Cart"
                value={productData?.totals?.totalAddsToCart || 0}
                icon={<AddShoppingCart />}
                subtitle={`${(productData?.dailyAverages?.avgAddsToCartPerDay || 0).toFixed(1)} avg/day`}
                color="warning"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MetricCard
                title="Purchases"
                value={productData?.totals?.totalPurchases || 0}
                icon={<ShoppingCart />}
                subtitle={`${(productData?.dailyAverages?.avgPurchasesPerDay || 0).toFixed(1)} avg/day`}
                color="success"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MetricCard
                title="Total Revenue"
                value={formatCurrency(productData?.totals?.totalRevenue || 0)}
                icon={<AttachMoney />}
                subtitle={`${formatCurrency(productData?.dailyAverages?.avgRevenuePerDay || 0)} avg/day`}
                color="primary"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>

    {/* Time Series Chart */}
    <Grid item xs={12} lg={8}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Product Performance Timeline
          </Typography>
          <Box sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={prepareProductTimeSeriesData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip 
                  formatter={(value, name) => [
                    name === 'revenue' ? formatCurrency(value) : value,
                    name === 'revenue' ? 'Revenue' : name
                  ]}
                />
                <Legend />
                <Bar 
                  yAxisId="left"
                  dataKey="views" 
                  fill="#8884d8" 
                  name="Views"
                  stackId="views"
                />
                <Bar 
                  yAxisId="left"
                  dataKey="addsToCart" 
                  fill="#ffc658" 
                  name="Adds to Cart"
                  stackId="adds"
                />
                <Bar 
                  yAxisId="left"
                  dataKey="purchases" 
                  fill="#82ca9d" 
                  name="Purchases"
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#ff7300" 
                  strokeWidth={2}
                  name="Revenue"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    </Grid>

    {/* Top Performing Products */}
    <Grid item xs={12} lg={4}>
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Top 5 Products by Revenue
          </Typography>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {productData?.productSummaries?.slice(0, 5).map((product, index) => (
              <Box 
                key={product.productId}
                sx={{ 
                  p: 2, 
                  mb: 1, 
                  borderRadius: 1,
                  bgcolor: index % 2 === 0 ? 'action.hover' : 'background.paper'
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" fontWeight="medium" noWrap>
                      {product.productName || `Product ${product.productId?.slice(-8)}`}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {product.categoryName || 'Uncategorized'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(product.revenue || 0)}
                  </Typography>
                </Box>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="textSecondary">
                      Views
                    </Typography>
                    <Typography variant="body2">
                      {formatNumber(product.views || 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="textSecondary">
                      Cart
                    </Typography>
                    <Typography variant="body2">
                      {formatNumber(product.addsToCart || 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="textSecondary">
                      Sales
                    </Typography>
                    <Typography variant="body2">
                      {formatNumber(product.purchases || 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="textSecondary">
                      Conv.
                    </Typography>
                    <Typography variant="body2">
                      {(product.conversionRate || 0).toFixed(1)}%
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ))}
            {(!productData?.productSummaries || productData.productSummaries.length === 0) && (
              <Typography color="textSecondary" sx={{ textAlign: 'center', p: 3 }}>
                No product data available
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Grid>

    {/* Product Performance Table */}
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Detailed Product Performance ({productData?.productSummaries?.length || 0} products)
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Currency</TableCell>
                  <TableCell align="right">Views</TableCell>
                  <TableCell align="right">Adds to Cart</TableCell>
                  <TableCell align="right">Cart Rate</TableCell>
                  <TableCell align="right">Purchases</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">Avg. Order</TableCell>
                  <TableCell align="right">Conversion</TableCell>
                  <TableCell align="right">Unique Users</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productData?.productSummaries
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((product) => (
                    <TableRow key={product.productId}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {product.productName || `Product ${product.productId?.slice(-8)}`}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {product.categoryName || 'Uncategorized'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={product.productCurrency || 'TZS'}
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={formatNumber(product.views || 0)}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={formatNumber(product.addsToCart || 0)}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" justifyContent="flex-end">
                          <Box sx={{ width: 60, mr: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(product.addToCartRate || 0, 100)}
                              color={
                                (product.addToCartRate || 0) > 10 ? 'success' : 
                                (product.addToCartRate || 0) > 5 ? 'warning' : 'error'
                              }
                            />
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            {(product.addToCartRate || 0).toFixed(1)}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={formatNumber(product.purchases || 0)}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="medium">
                          {formatCurrency(product.revenue || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {product.purchases > 0 ? (
                          <Typography variant="body2">
                            {formatCurrency(product.avgOrderValue || 0)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" justifyContent="flex-end">
                          <Box sx={{ width: 60, mr: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(product.conversionRate || 0, 100)}
                              color={
                                (product.conversionRate || 0) > 5 ? 'success' : 
                                (product.conversionRate || 0) > 2 ? 'warning' : 'error'
                              }
                            />
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            {(product.conversionRate || 0).toFixed(1)}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={formatNumber(product.uniqueUsersCount || 0)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={productData?.productSummaries?.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>
    </Grid>
  </Grid>
)}

      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Category Performance
                </Typography>
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data?.categories?.slice(0, 10) || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="category" />
                      <YAxis />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip formatter={(value, name) => [
                        typeof value === 'number' ? formatNumber(value) : value,
                        name === 'revenue' ? 'Revenue' : name.charAt(0).toUpperCase() + name.slice(1)
                      ]} />
                      <Legend />
                      <Bar dataKey="views" fill="#8884d8" name="Views" />
                      <Bar dataKey="productViews" fill="#82ca9d" name="Product Views" />
                      <Bar dataKey="purchases" fill="#ffc658" name="Purchases" />
                      <Line 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="#ff7300" 
                        name="Revenue"
                        yAxisId="right"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Category Stats
                </Typography>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {data?.categories?.map((category, index) => (
                    <Box 
                      key={category.category || index} 
                      sx={{ 
                        p: 2, 
                        mb: 1, 
                        borderRadius: 1,
                        bgcolor: index % 2 === 0 ? 'action.hover' : 'background.paper'
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" fontWeight="medium">
                          {category.category || 'Unknown'}
                        </Typography>
                        <Chip 
                          label={formatCurrency(category.revenue || 0)} 
                          size="small" 
                          color="primary"
                        />
                      </Box>
                      <Grid container spacing={1} sx={{ mt: 1 }}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="textSecondary">
                            Views
                          </Typography>
                          <Typography variant="body2">
                            {formatNumber(category.views || 0)}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="textSecondary">
                            Purchases
                          </Typography>
                          <Typography variant="body2">
                            {formatNumber(category.purchases || 0)}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="textSecondary">
                            Conv. Rate
                          </Typography>
                          <Typography variant="body2">
                            {category.views > 0 ? ((category.purchases / category.views * 100) || 0).toFixed(1) : 0}%
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  User Engagement Leaderboard
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell align="right">Sessions</TableCell>
                        <TableCell align="right">Total Events</TableCell>
                        <TableCell align="right">Purchases</TableCell>
                        <TableCell align="right">Revenue</TableCell>
                        <TableCell align="right">Engagement Score</TableCell>
                        <TableCell align="right">Last Active</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data?.userEngagement?.map((user, index) => (
                        <TableRow key={user.userId || index}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {user.userName || user.userEmail || `User ${user.userId?.slice(-8)}`}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {user.userEmail || 'No email'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(user.sessionCount || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(user.totalEvents || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(user.purchases || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(user.revenue || 0)}
                          </TableCell>
                          <TableCell align="right">
                            <Box display="flex" alignItems="center" justifyContent="flex-end">
                              <Box sx={{ width: 100, mr: 1 }}>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={Math.min(user.engagementScore || 0, 100)}
                                  color={
                                    (user.engagementScore || 0) > 80 ? 'success' : 
                                    (user.engagementScore || 0) > 50 ? 'warning' : 'error'
                                  }
                                />
                              </Box>
                              <Typography variant="body2">
                                {Math.round(user.engagementScore || 0)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="caption">
                              {user.lastSeen ? format(new Date(user.lastSeen), 'MMM dd, HH:mm') : 'Never'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Device Usage Distribution
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={prepareDeviceData()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.device}: ${formatNumber(entry.sessions)}`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="sessions"
                      >
                        {prepareDeviceData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Device Revenue Contribution
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={prepareDeviceData()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="device" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="revenue" fill="#82ca9d" name="Revenue" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Device Performance Details
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Device Type</TableCell>
                        <TableCell align="right">Sessions</TableCell>
                        <TableCell align="right">Unique Users</TableCell>
                        <TableCell align="right">Purchases</TableCell>
                        <TableCell align="right">Revenue</TableCell>
                        <TableCell align="right">Avg. Order Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data?.devices?.map((device, index) => (
                        <TableRow key={device.deviceType || index}>
                          <TableCell>
                            <Chip 
                              icon={<Devices />}
                              label={device.deviceType || 'Unknown'}
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(device.count || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(device.uniqueUsersCount || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(device.purchases || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(device.revenue || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {device.purchases > 0 ? formatCurrency((device.revenue || 0) / (device.purchases || 1)) : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}


{activeTab === 5 && (
  <Grid container spacing={3}>
    <Grid item xs={12} md={4}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Real-time Stats
          </Typography>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                  <Typography variant="h4">
                    {data?.realtime?.activeSessions || 0}
                  </Typography>
                  <Typography variant="caption">Active Sessions (5 min)</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                  <Typography variant="h4">
                    {data?.realtime?.eventsLast5Min || 0}
                  </Typography>
                  <Typography variant="caption">Events (5 min)</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
                  <Typography variant="h6">
                    Last Hour Summary
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {data?.realtime?.interactionsByType?.reduce((sum, item) => sum + (item.count || 0), 0) || 0} total events
                  </Typography>
                  <Typography variant="caption">
                    Across {data?.realtime?.interactionsByType?.length || 0} event types
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 3, textAlign: 'center' }}>
              Last updated: {new Date().toLocaleTimeString()}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Grid>
    
    <Grid item xs={12} md={8}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Recent Activity (Last 5 Minutes)
            </Typography>
            <Chip 
              label="Live" 
              color="error" 
              size="small"
              sx={{ animation: 'pulse 2s infinite' }}
            />
          </Box>
          <Box sx={{ height: 300, overflow: 'auto' }}>
            {data?.realtime?.popularPages?.map((page, index) => (
              <Box 
                key={index}
                sx={{ 
                  p: 1.5, 
                  mb: 1, 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Typography variant="body2" noWrap sx={{ maxWidth: '70%' }}>
                  {page.page || 'Unknown Page'}
                </Typography>
                <Box display="flex" gap={2}>
                  <Chip 
                    label={`${page.views || 0} views`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip 
                    label={`${page.uniqueSessions || 0} sessions`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                </Box>
              </Box>
            ))}
            {(!data?.realtime?.popularPages || data.realtime.popularPages.length === 0) && (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography color="textSecondary">
                  No recent activity data available
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Grid>
    
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Activity Trends (Last Hour)
            </Typography>
            <Chip 
              label={`${format(new Date(Date.now() - 60 * 60 * 1000), 'HH:mm')} - ${format(new Date(), 'HH:mm')}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          <Box sx={{ height: 200 }}>
            {data?.realtime?.interactionsByType && data.realtime.interactionsByType.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.realtime.interactionsByType}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="_id" 
                    tickFormatter={(value) => {
                      // Shorten long interaction type names
                      const maxLength = 15;
                      if (value.length > maxLength) {
                        return value.substring(0, maxLength) + '...';
                      }
                      return value;
                    }}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [`${value} events`, 'Count']}
                    labelFormatter={(label) => `Type: ${label}`}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="#8884d8" 
                    name="Event Count"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 2
              }}>
                <Typography color="textSecondary">
                  No activity data for the last hour
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  This could mean:
                </Typography>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="textSecondary">
                    1. No users have interacted with your app in the last hour
                  </Typography>
                  <br />
                  <Typography variant="caption" color="textSecondary">
                    2. Analytics tracking isn't properly configured
                  </Typography>
                  <br />
                  <Typography variant="caption" color="textSecondary">
                    3. The data is still processing
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
          {data?.realtime?.interactionsByType && data.realtime.interactionsByType.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={1}>
                {data.realtime.interactionsByType.slice(0, 6).map((item, index) => (
                  <Grid item xs={6} sm={4} md={2} key={index}>
                    <Paper sx={{ p: 1, textAlign: 'center' }}>
                      <Typography variant="caption" color="textSecondary">
                        {item._id}
                      </Typography>
                      <Typography variant="h6">
                        {formatNumber(item.count || 0)}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>
    </Grid>
    
    {/* Add debug info for developers */}
    {process.env.NODE_ENV === 'development' && (
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="textSecondary">
              Debug Information
            </Typography>
            <pre style={{ 
              fontSize: '12px', 
              backgroundColor: '#f5f5f5', 
              padding: '10px',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '200px'
            }}>
              {JSON.stringify({
                realtimeData: data?.realtime,
                timestamp: new Date().toISOString(),
                lastHourStart: format(new Date(Date.now() - 60 * 60 * 1000), 'yyyy-MM-dd HH:mm:ss'),
                interactionsByTypeLength: data?.realtime?.interactionsByType?.length || 0,
                totalEvents: data?.realtime?.interactionsByType?.reduce((sum, item) => sum + (item.count || 0), 0) || 0
              }, null, 2)}
            </pre>
          </CardContent>
        </Card>
      </Grid>
    )}
  </Grid>
)}

{activeTab === 6 && ( // Assuming 6 is a new tab for Cart Analytics
  <Grid container spacing={3}>
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">
              Cart Analytics (Last Hour)
            </Typography>
            <Chip 
              label={`${format(new Date(Date.now() - 60 * 60 * 1000), 'HH:mm')} - ${format(new Date(), 'HH:mm')}`}
              color="primary"
              icon={<ShoppingCart />}
            />
          </Box>
          
          {/* Cart Summary Metrics */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Add to Cart"
                value={cartData?.summary?.totalAddToCart || 0}
                icon={<ShoppingCart />}
                color="warning"
                subtitle={`${cartData?.summary?.uniqueUsersAdded || 0} unique users`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Remove from Cart"
                value={cartData?.summary?.totalRemoveFromCart || 0}
                icon={<RemoveShoppingCart />} // You'll need to import this icon
                color="error"
                subtitle="Items removed"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Net Additions"
                value={cartData?.summary?.netCartAdditions || 0}
                icon={<Add />} // You'll need to import this icon
                color="success"
                subtitle="Positive = more adds than removes"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Abandonment Rate"
                value={`${(cartData?.summary?.cartAbandonmentRate || 0).toFixed(1)}%`}
                icon={<Warning />} // You'll need to import this icon
                color="info"
                subtitle="Carts not converted"
              />
            </Grid>
          </Grid>

          {/* Cart Activity Chart */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Cart Activity Over Time (Last Hour)
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={cartData?.cartEventsByTime || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="time" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Area 
                          type="monotone" 
                          dataKey="addToCart" 
                          stackId="1"
                          stroke="#8884d8" 
                          fill="#8884d8" 
                          name="Add to Cart"
                        />
                        <Area 
                          type="monotone" 
                          dataKey="removeFromCart" 
                          stackId="1"
                          stroke="#82ca9d" 
                          fill="#82ca9d" 
                          name="Remove from Cart"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Top Products in Cart
                  </Typography>
                  <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {cartData?.topProducts?.map((product, index) => (
                      <Box 
                        key={product.productId}
                        sx={{ 
                          p: 2, 
                          mb: 1, 
                          borderRadius: 1,
                          bgcolor: index % 2 === 0 ? 'action.hover' : 'background.paper',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight="medium" noWrap>
                            {product.productName || `Product ${index + 1}`}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {product.uniqueUsersCount || 0} users
                          </Typography>
                        </Box>
                        <Chip 
                          label={`${product.count || 0} adds`}
                          size="small"
                          color="warning"
                        />
                      </Box>
                    ))}
                    {(!cartData?.topProducts || cartData.topProducts.length === 0) && (
                      <Typography color="textSecondary" sx={{ textAlign: 'center', p: 2 }}>
                        No cart activity in the last hour
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
)}

{activeTab === 7 && ( // Assuming 7 is a new tab for Purchase Analytics
  <Grid container spacing={3}>
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">
              Purchase Analytics (Last Hour)
            </Typography>
            <Chip 
              label={`${format(new Date(Date.now() - 60 * 60 * 1000), 'HH:mm')} - ${format(new Date(), 'HH:mm')}`}
              color="success"
              icon={<AttachMoney />}
            />
          </Box>
          
          {/* Purchase Summary Metrics */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Purchases"
                value={purchaseData?.summary?.totalPurchases || 0}
                icon={<ShoppingCart />}
                color="success"
                subtitle={`${purchaseData?.summary?.uniqueBuyers || 0} unique buyers`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Revenue"
                value={formatCurrency(purchaseData?.summary?.totalRevenue || 0)}
                icon={<AttachMoney />}
                color="primary"
                subtitle="Last hour revenue"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Avg Order Value"
                value={formatCurrency(purchaseData?.summary?.avgOrderValue || 0)}
                icon={<TrendingUp />}
                color="info"
                subtitle="Average per purchase"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Conversion Rate"
                value={`${purchaseData?.summary?.conversionRate || 0}%`}
                icon={<CheckCircle />} // You'll need to import this icon
                color="warning"
                subtitle="Buyer conversion"
              />
            </Grid>
          </Grid>

          {/* Purchase Activity Chart */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Purchase Activity Over Time (Last Hour)
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={purchaseData?.purchaseEventsByTime || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="time" />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip formatter={(value, name) => [
                          name === 'revenue' ? formatCurrency(value) : value,
                          name === 'revenue' ? 'Revenue' : 'Purchases'
                        ]} />
                        <Legend />
                        <Bar 
                          yAxisId="left"
                          dataKey="purchases" 
                          fill="#82ca9d" 
                          name="Purchases"
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="revenue" 
                          stroke="#8884d8" 
                          name="Revenue"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Revenue by Category
                  </Typography>
                  <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {purchaseData?.revenueByProductCategory?.map((category, index) => (
                      <Box 
                        key={category.category}
                        sx={{ 
                          p: 2, 
                          mb: 1, 
                          borderRadius: 1,
                          bgcolor: index % 2 === 0 ? 'action.hover' : 'background.paper'
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" fontWeight="medium">
                            {category.category}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(category.revenue || 0)}
                          </Typography>
                        </Box>
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="textSecondary">
                              Items Sold
                            </Typography>
                            <Typography variant="body2">
                              {category.itemsSold || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="textSecondary">
                              Avg Price
                            </Typography>
                            <Typography variant="body2">
                              {formatCurrency(category.avgPrice || 0)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                    {(!purchaseData?.revenueByProductCategory || purchaseData.revenueByProductCategory.length === 0) && (
                      <Typography color="textSecondary" sx={{ textAlign: 'center', p: 2 }}>
                        No purchase data by category
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Top Buyers Table */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Buyers (Last Hour)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Buyer</TableCell>
                      <TableCell align="right">Purchases</TableCell>
                      <TableCell align="right">Total Spent</TableCell>
                      <TableCell align="right">Avg Order Value</TableCell>
                      <TableCell align="right">Unique Products</TableCell>
                      <TableCell align="right">Last Purchase</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchaseData?.topBuyers?.map((buyer, index) => (
                      <TableRow key={buyer.userId}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {buyer.userName || buyer.userEmail || `User ${buyer.userId?.slice(-8)}`}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {buyer.userEmail || 'No email'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={buyer.purchaseCount}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="medium">
                            {formatCurrency(buyer.totalSpent || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(buyer.avgOrderValue || 0)}
                        </TableCell>
                        <TableCell align="right">
                          {buyer.uniqueProductsCount || 0}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption">
                            {buyer.lastPurchase ? format(new Date(buyer.lastPurchase), 'HH:mm') : 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!purchaseData?.topBuyers || purchaseData.topBuyers.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ p: 3 }}>
                          <Typography color="textSecondary">
                            No purchase data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
)}

{activeTab === 8 && (
  <Grid container spacing={3}>
    {/* Header */}
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h5" gutterBottom>
                Progressive Web App Analytics
              </Typography>
              <Typography color="textSecondary">
                Track PWA installations, launches, and user engagement
              </Typography>
            </Box>
            <Box display="flex" gap={2}>
              <Chip 
                label={`${formatDateForAPI(startDate)} to ${formatDateForAPI(endDate)}`}
                color="primary"
                variant="outlined"
              />
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={fetchPWAnalytics}
                disabled={loadingPWA}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {/* Summary Metrics */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Installs"
                value={pwaData?.summary?.installs?.totalInstalls || 0}
                icon={<InstallDesktop />}
                subtitle={`${pwaData?.summary?.metrics?.uniqueInstallingUsersCount || 0} unique users`}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Launches"
                value={pwaData?.summary?.launches?.totalLaunches || 0}
                icon={<RocketLaunch />}
                subtitle={`${pwaData?.summary?.metrics?.uniqueLaunchingUsersCount || 0} unique users`}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Conversion Rate"
                value={`${(pwaData?.summary?.metrics?.conversionRate || 0).toFixed(1)}%`}
                icon={<TrendingUp />}
                subtitle="Installs to Launches"
                color="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Avg Launches/User"
                value={(pwaData?.summary?.metrics?.avgLaunchesPerUser || 0).toFixed(1)}
                icon={<Repeat />} 
                subtitle="Engagement rate"
                color="warning"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>

    {/* Timeline Chart */}
    <Grid item xs={12} lg={8}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            PWA Activity Timeline
          </Typography>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={preparePWATimelineData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="installs"
                  stroke="#8884d8"
                  strokeWidth={2}
                  name="Installs"
                  dot={{ r: 4 }}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="launches"
                  stroke="#82ca9d"
                  strokeWidth={2}
                  name="Launches"
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    </Grid>

    {/* Device & Platform Breakdown */}
    <Grid item xs={12} lg={4}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Installs by Device
              </Typography>
              <Box sx={{ height: 140 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={preparePWADeviceData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="device" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="installs" fill="#8884d8" name="Installs" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Launches by Platform
              </Typography>
              <Box sx={{ height: 140 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={preparePWAPlatformData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.platform}: ${entry.launches}`}
                      outerRadius={50}
                      fill="#8884d8"
                      dataKey="launches"
                    >
                      {preparePWAPlatformData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Grid>

    {/* Launch Type Analysis */}
    <Grid item xs={12} md={6}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Launch Type Distribution
          </Typography>
          <Box sx={{ height: 250, p: 2 }}>
            {pwaData?.summary?.launches?.launchTypes?.length > 0 ? (
              <Grid container spacing={1}>
                {pwaData.summary.launches.launchTypes.map((type, index) => (
                  <Grid item xs={12} key={type}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">
                        {type === 'standalone' ? 'Standalone App' : 
                         type === 'browser' ? 'Browser Tab' : 
                         type || 'Unknown'}
                      </Typography>
                      <Chip 
                        label={`${Math.round((index + 1) / pwaData.summary.launches.launchTypes.length * 100)}%`}
                        size="small"
                        color="primary"
                      />
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(index + 1) / pwaData.summary.launches.launchTypes.length * 100}
                      color={index === 0 ? 'success' : index === 1 ? 'info' : 'warning'}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="textSecondary">
                  No launch type data available
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Grid>

    {/* Top Engaging Users */}
    <Grid item xs={12} md={6}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Top Engaging Users
          </Typography>
          <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
            {pwaData?.summary?.userEngagement?.map((user, index) => (
              <Box 
                key={user.userId}
                sx={{ 
                  p: 1.5, 
                  mb: 1, 
                  borderRadius: 1,
                  bgcolor: index % 2 === 0 ? 'action.hover' : 'background.paper',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight="medium" noWrap>
                    {user.userName || user.userEmail || `User ${user.userId?.slice(-8)}`}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {user.launchCount} launches
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Chip 
                    label={`${user.launchFrequency?.toFixed(1)}/day`}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                  <Chip 
                    label={`${user.deviceCount} devices`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                </Box>
              </Box>
            ))}
            {(!pwaData?.summary?.userEngagement || pwaData.summary.userEngagement.length === 0) && (
              <Typography color="textSecondary" sx={{ textAlign: 'center', p: 3 }}>
                No user engagement data available
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Grid>

    {/* Detailed Timeline Table */}
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Daily Activity Details
            </Typography>
            <Chip 
              label={`Last ${pwaData?.summary?.dailyTrends?.length || 0} days`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Installs</TableCell>
                  <TableCell align="right">Unique Installing Users</TableCell>
                  <TableCell align="right">Launches</TableCell>
                  <TableCell align="right">Unique Launching Users</TableCell>
                  <TableCell align="right">Launch/Install Ratio</TableCell>
                  <TableCell align="right">Trend</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pwaData?.timeline?.slice(0, 15).map((day) => (
                  <TableRow key={day.date}>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(day.date), 'MMM dd, yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={day.installs?.count || 0}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {day.installs?.uniqueUsers?.length || 0}
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={day.launches?.count || 0}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {day.launches?.uniqueUsers?.length || 0}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {day.installs?.count > 0 
                          ? ((day.launches?.count || 0) / day.installs.count).toFixed(2)
                          : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {getPWATrendIcon(day)}
                    </TableCell>
                  </TableRow>
                ))}
                {(!pwaData?.timeline || pwaData.timeline.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ p: 3 }}>
                      <Typography color="textSecondary">
                        No PWA activity data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
)}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AnalyticsDashboard;